import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { call } from "../../api/client.js";
import { Button } from "../../components/ui/Button.jsx";
import { Switch } from "../../components/ui/Switch.jsx";
import { PageLoader } from "../../components/ui/Spinner.jsx";

/**
 * Site-wide settings — currently only page-visibility toggles. Hiding a
 * page here removes the nav link in the public site Header + Footer and
 * makes the route redirect to /home-designs when accessed directly.
 *
 * Brand info is hard-coded in the frontend (see frontend/src/lib/brand.js).
 * If you need that to be editable again, add it back to TABS here and
 * surface the same fields on the public settings endpoint.
 */
export default function SettingsPage() {
  const queryClient = useQueryClient();

  useEffect(() => {
    document.title = "Settings · Admin";
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: () => call("get", "/admin/settings"),
    staleTime: 30_000,
  });

  const update = useMutation({
    mutationFn: (payload) => call("patch", "/admin/settings", { data: payload }),
    onSuccess: () => {
      toast.success("Settings saved");
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      // Public site cache is keyed at the frontend layer; users will see
      // the change after the next time their tab refetches settings.
    },
    onError: (err) => toast.error(err?.response?.data?.message ?? "Failed to save"),
  });

  if (isLoading || !data) return <PageLoader />;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-widest text-accent">System</p>
        <h1 className="mt-1 font-display text-2xl text-text-primary">Settings</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Control which pages of the public site are visible. Disabled pages disappear from the
          main navigation and footer; their URLs redirect to the home-designs catalogue.
        </p>
      </div>

      <PageVisibilityForm
        initial={data.pageVisibility ?? { displayHomes: true, houseLand: true }}
        onSave={(values) => update.mutate({ pageVisibility: values })}
        isSaving={update.isPending}
      />
    </div>
  );
}

function PageVisibilityForm({ initial, onSave, isSaving }) {
  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = useForm({ defaultValues: initial });

  useEffect(() => {
    if (initial) reset(initial);
  }, [initial, reset]);

  return (
    <form onSubmit={handleSubmit(onSave)} className="space-y-4" noValidate>
      <div className="rounded-2xl border border-border-subtle bg-bg-elevated p-6">
        <div className="mb-5 flex items-center gap-2 text-sm text-text-muted">
          <Eye className="h-4 w-4" />
          Page visibility
        </div>

        <div className="space-y-1">
          <Controller
            control={control}
            name="displayHomes"
            render={({ field }) => (
              <VisibilityRow
                title="Display Homes"
                description="The /display-homes index + each /display-homes/<slug> detail page. When hidden, also drops the entry from the Display Homes mega-menu."
                checked={Boolean(field.value)}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            control={control}
            name="houseLand"
            render={({ field }) => (
              <VisibilityRow
                title="House &amp; Land"
                description="The /house-land index + each /house-land/<slug> detail page. When hidden, the page redirects to /home-designs."
                checked={Boolean(field.value)}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" loading={isSaving} disabled={!isDirty}>
          Save changes
        </Button>
      </div>
    </form>
  );
}

function VisibilityRow({ title, description, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-border-subtle/40 py-4 last:border-b-0">
      <div className="flex items-start gap-3 min-w-0">
        <span
          className={
            checked
              ? "mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-md bg-accent/15 text-accent"
              : "mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-md bg-bg-elevated-2 text-text-muted"
          }
        >
          {checked ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </span>
        <div className="min-w-0">
          <p className="font-medium text-text-primary">{title}</p>
          <p className="mt-0.5 text-xs text-text-secondary">{description}</p>
        </div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        label={checked ? "Visible" : "Hidden"}
      />
    </div>
  );
}
